import { apiClient } from '@/api/client'
import {
  ANNOUNCEMENTS,
  ANNOUNCEMENTS_BANNER,
  announcementByIdPath,
  announcementPublishPath,
  announcementArchivePath,
  announcementDismissPath,
  PLATFORM_ANNOUNCEMENTS_ACTIVE,
  platformAnnouncementDismissPath,
} from '@/api/endpoints'
import type { ApiResponseWrapper } from '@/types/api.types'
import type {
  Announcement,
  AnnouncementsListResponse,
  CreateAnnouncementPayload,
  UpdateAnnouncementPayload,
  AnnouncementFilterParams,
  PlatformAnnouncement,
} from '@/types/announcement.types'

export async function getAnnouncements(
  params?: AnnouncementFilterParams
): Promise<AnnouncementsListResponse> {
  const { data } = await apiClient.get<ApiResponseWrapper<AnnouncementsListResponse>>(
    ANNOUNCEMENTS,
    { params }
  )
  const payload = data?.data
  if (payload && typeof payload === 'object' && Array.isArray(payload.data)) {
    return payload
  }
  return { data: [], total: 0 }
}

export async function getBannerAnnouncements(): Promise<AnnouncementsListResponse> {
  const { data } = await apiClient.get<ApiResponseWrapper<AnnouncementsListResponse>>(
    ANNOUNCEMENTS_BANNER
  )
  const payload = data?.data
  if (payload && typeof payload === 'object' && Array.isArray(payload.data)) {
    return payload
  }
  return { data: [], total: 0 }
}

export async function getAnnouncement(id: string): Promise<Announcement> {
  const { data } = await apiClient.get<ApiResponseWrapper<Announcement>>(
    announcementByIdPath(id)
  )
  if (data?.data && typeof data.data === 'object' && data.data._id) {
    return data.data
  }
  throw new Error('Announcement not found')
}

export async function createAnnouncement(
  payload: CreateAnnouncementPayload
): Promise<Announcement> {
  const { data } = await apiClient.post<ApiResponseWrapper<Announcement>>(
    ANNOUNCEMENTS,
    payload
  )
  if (data?.data && typeof data.data === 'object' && data.data._id) {
    return data.data
  }
  throw new Error('Failed to create announcement')
}

export async function updateAnnouncement(
  id: string,
  payload: UpdateAnnouncementPayload
): Promise<Announcement> {
  const { data } = await apiClient.patch<ApiResponseWrapper<Announcement>>(
    announcementByIdPath(id),
    payload
  )
  if (data?.data && typeof data.data === 'object' && data.data._id) {
    return data.data
  }
  throw new Error('Failed to update announcement')
}

export async function publishAnnouncement(id: string): Promise<Announcement> {
  const { data } = await apiClient.post<ApiResponseWrapper<Announcement>>(
    announcementPublishPath(id)
  )
  if (data?.data && typeof data.data === 'object' && data.data._id) {
    return data.data
  }
  throw new Error('Failed to publish announcement')
}

export async function archiveAnnouncement(id: string): Promise<Announcement> {
  const { data } = await apiClient.post<ApiResponseWrapper<Announcement>>(
    announcementArchivePath(id)
  )
  if (data?.data && typeof data.data === 'object' && data.data._id) {
    return data.data
  }
  throw new Error('Failed to archive announcement')
}

export async function deleteAnnouncement(id: string): Promise<void> {
  await apiClient.delete(announcementByIdPath(id))
}

export async function dismissAnnouncement(id: string): Promise<void> {
  await apiClient.post(announcementDismissPath(id))
}

/**
 * Announcements from the platform operator.
 *
 * Kept beside the tenant feed rather than merged server-side: the two live in
 * different collections with different lifecycles, and one banner joining them
 * on the client is cheaper than one endpoint pretending they are one thing.
 */
export async function getPlatformAnnouncements(): Promise<PlatformAnnouncement[]> {
  const { data } = await apiClient.get<ApiResponseWrapper<{ items: PlatformAnnouncement[] }>>(
    PLATFORM_ANNOUNCEMENTS_ACTIVE
  )
  return data?.data?.items ?? []
}

export async function dismissPlatformAnnouncement(id: string): Promise<void> {
  await apiClient.post(platformAnnouncementDismissPath(id))
}
